import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { CreateCompanyResponse } from 'types/organizationTypes';

interface CreateOrganizationFormContextType {
  companyData: CreateCompanyResponse | null;
  setCompanyData: (data: CreateCompanyResponse) => void;
  isEditingAddress: boolean;
  setIsEditingAddress: (value: boolean) => void;
  isEditingLocation: boolean;
  setIsEditingLocation: (value: boolean) => void;
  hasToResetCollapse: boolean;
  setHasToResetCollapse: (value: boolean) => void;
}

export const CreateOrganizationFormContext =
  createContext<CreateOrganizationFormContextType>({
    companyData: {
      name: '',
      description: '',
      fiscalId: '',
      contractAddress: '',
      isPrimary: false,
      account: {
        id: '',
        name: '',
        createdAt: '',
        updatedAt: '',
      },
      organizationKey: '',
      txHash: '',
      id: '',
      createdAt: '',
      updatedAt: '',
    },
    setCompanyData: () => {},
    isEditingAddress: false,
    setIsEditingAddress: () => {},
    isEditingLocation: false,
    setIsEditingLocation: () => {},
    hasToResetCollapse: false,
    setHasToResetCollapse: () => {},
  });

type CreateOrganizationFormContextProviderProps = {
  children: ReactNode;
};

export const CreateOrganizationFormContextProvider = ({
  children,
}: CreateOrganizationFormContextProviderProps) => {
  // Información de la company al crearse
  const [companyData, setCompanyData] = useState<CreateCompanyResponse>({
    name: '',
    description: '',
    fiscalId: '',
    contractAddress: '',
    isPrimary: false,
    account: {
      id: '',
      name: '',
      createdAt: '',
      updatedAt: '',
    },
    organizationKey: '',
    txHash: '',
    id: '',
    createdAt: '',
    updatedAt: '',
  });

  // Wrapper para setCompanyData
  const handleSetCompany = useCallback((data: CreateCompanyResponse) => {
    setCompanyData(data);
  }, []);

  // Flag para chequear si está creando o editando un address
  const [isEditingAddress, setIsEditingAddress] = useState<boolean>(false);

  // Wrapper para setIsEditingAddress
  const handleSetIsEditingAddress = useCallback((value: boolean) => {
    setIsEditingAddress(value);
  }, []);

  // Flag para chequear si está creando o editando un location
  const [isEditingLocation, setIsEditingLocation] = useState<boolean>(false);

  // Wrapper para setIsEditingLocation
  const handleSetIsEditingLocation = useCallback((value: boolean) => {
    setIsEditingLocation(value);
  }, []);

  // Flag para chequear si es necesario reiniciar el state del collapse
  const [hasToResetCollapse, setHasToResetCollapse] = useState<boolean>(false);

  // Wrapper para setHasToResetCollapse
  const handleSetHasToResetCollapse = useCallback((value: boolean) => {
    setHasToResetCollapse(value);
  }, []);

  return (
    <CreateOrganizationFormContext.Provider
      value={{
        companyData,
        setCompanyData: handleSetCompany,
        isEditingAddress,
        setIsEditingAddress: handleSetIsEditingAddress,
        isEditingLocation,
        setIsEditingLocation: handleSetIsEditingLocation,
        hasToResetCollapse,
        setHasToResetCollapse: handleSetHasToResetCollapse,
      }}
    >
      {children}
    </CreateOrganizationFormContext.Provider>
  );
};

// Hook personalizado
export const useCreateOrganizationFormContext = () => {
  const context = useContext(CreateOrganizationFormContext);
  if (context === undefined) {
    throw new Error(
      'useCreateOrganizationFormContext must be used within a CreateOrganizationFormContextProvider'
    );
  }
  return context;
};
